import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  MessageSquare, ArrowLeft, Send, Loader2, Search, UserCircle, Clock,
  ChevronRight, Bell, Eye, Swords, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { BadgeCelebration } from '../components/BadgeCelebration';

// ============ CONVERSATION LIST ============
function ConversationList({ conversations, selected, onSelect, language, isSuperAdmin }) {
  const [search, setSearch] = useState('');

  const filtered = conversations.filter(c => {
    if (!search) return true;
    const name = isSuperAdmin ? `${c.coach_name} ${c.client_name}` : (c.client_name || c.coach_name || '');
    return name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="flex flex-col h-full" data-testid="conversation-list">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-secondary border-input h-9 text-sm"
            data-testid="conversation-search"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">
            {language === 'fr' ? 'Aucune conversation' : 'No conversations'}
          </div>
        ) : (
          filtered.map(c => {
            const isActive = selected === c.conversation_id;
            const unread = isSuperAdmin ? c.coach_unread : (c.coach_unread || c.client_unread || 0);
            return (
              <button
                key={c.conversation_id}
                onClick={() => onSelect(c.conversation_id)}
                className={`w-full p-3 text-left border-b border-border/50 transition-all ${
                  isActive ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-secondary/50'
                }`}
                data-testid={`conversation-${c.conversation_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <UserCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-foreground truncate">
                        {isSuperAdmin ? `${c.coach_name} → ${c.client_name}` : (c.client_name || c.coach_name || '—')}
                      </p>
                      {unread > 0 && (
                        <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                          {unread}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {c.last_message || (language === 'fr' ? 'Nouvelle conversation' : 'New conversation')}
                    </p>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ============ CHAT VIEW ============
function ChatView({ conversationId, language, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [convo, setConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get(`/messages/conversation/${conversationId}`);
      setMessages(res.data.messages);
      setConvo(res.data.conversation);
    } catch {
      toast.error('Erreur chargement messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setLoading(true);
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const recipientId = convo.coach_id === currentUserId ? convo.client_id : convo.coach_id;
      await api.post('/messages/send', {
        recipient_id: recipientId,
        content: newMessage.trim()
      });
      setNewMessage('');
      await fetchMessages();
    } catch {
      toast.error('Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-full"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      {/* Chat Header */}
      <div className="p-3 border-b border-border bg-card flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
          <UserCircle className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="text-sm font-bold text-foreground">
            {convo?.coach_id === currentUserId ? convo?.client_name : convo?.coach_name}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {language === 'fr' ? 'Messagerie privee' : 'Private messaging'}
          </p>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-area">
        {messages.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-8">
            {language === 'fr' ? 'Debut de la conversation' : 'Start of conversation'}
          </div>
        )}
        {messages.map((msg) => {
          const isOwn = msg.sender_id === currentUserId;
          return (
            <div key={msg.message_id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.message_id}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                  isOwn
                    ? 'text-primary-foreground rounded-br-md'
                    : 'text-foreground rounded-bl-md'
                }`}
                style={
                  isOwn
                    ? {
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.90), rgba(107,33,168,0.90))',
                        border: '1px solid rgba(192,132,252,0.40)',
                        boxShadow: '0 0 18px rgba(168,85,247,0.30), inset 0 0 0 1px rgba(216,180,254,0.10)',
                      }
                    : {
                        background: 'linear-gradient(135deg, rgba(28,12,5,0.85), rgba(15,3,30,0.95))',
                        border: '1px solid rgba(251,146,60,0.30)',
                        boxShadow: '0 0 16px rgba(251,146,60,0.18), inset 0 0 0 1px rgba(251,146,60,0.08)',
                        backdropFilter: 'blur(6px)',
                      }
                }
              >
                {!isOwn && (
                  <p className="text-[10px] font-bold mb-0.5 opacity-70">{msg.sender_name}</p>
                )}
                <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <Clock className="w-2.5 h-2.5 opacity-50" />
                  <span className="text-[9px] opacity-50">
                    {new Date(msg.created_at).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 border-t border-border bg-card">
        <div className="flex items-end gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={language === 'fr' ? 'Ecris ton message...' : 'Type your message...'}
            className="min-h-[44px] max-h-[120px] resize-none bg-secondary border-input text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            data-testid="message-input"
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            size="sm"
            className="h-11 w-11 p-0 bg-primary"
            data-testid="send-message-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ NOTIFICATIONS PANEL ============
function NotificationsPanel({ language, onSelectClient }) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [celebrationBadge, setCelebrationBadge] = useState(null);

  const fetchNotifs = useCallback(() => {
    api.get('/coach/notifications')
      .then(res => {
        setNotifications(res.data.notifications);
        setUnread(res.data.unread_count);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  const markAllRead = async () => {
    await api.post('/coach/notifications/mark-all-read');
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  const approveReturn = async (notif) => {
    const userId = notif.data?.user_id;
    const zone = notif.data?.zone;
    if (!userId || !zone) return;

    setApproving(notif.notification_id || `${userId}-${zone}`);
    try {
      const res = await api.post('/injuries/approve-return', { user_id: userId, zone });
      const name = res.data.member_name || notif.data?.user_name || 'Membre';
      const tierLabel = res.data.badge_tier_label || 'Acier';
      const tierNum = res.data.badge_tier || 1;

      // Trigger golden explosion celebration for Tier Or (3)
      if (tierNum >= 3 && res.data.badge_awarded) {
        setCelebrationBadge({
          name: `SURVIVANT x${tierNum}`,
          description: language === 'fr'
            ? `${name} a survecu ${tierNum} blessures. Legende forgee dans le feu.`
            : `${name} survived ${tierNum} injuries. Legend forged in fire.`,
          icon: '🛡️',
          rarity: 'legendary',
          survivantTier: tierNum,
          survivantCount: tierNum,
        });
      } else if (tierNum >= 2 && res.data.badge_awarded) {
        // Bronze tier — standard legendary celebration
        setCelebrationBadge({
          name: `SURVIVANT x${tierNum}`,
          description: language === 'fr'
            ? `${name} passe au niveau ${tierLabel}. Un accomplissement bien merite.`
            : `${name} reached ${tierLabel} tier. A well-deserved achievement.`,
          icon: '🛡️',
          rarity: 'legendary',
          survivantTier: tierNum,
          survivantCount: tierNum,
        });
      }

      toast.success(
        language === 'fr'
          ? `${name} redeploye! Badge SURVIVANT ${tierLabel} (x${tierNum}) ${res.data.badge_awarded ? 'decerne' : 'confirme'}.`
          : `${name} redeployed! SURVIVOR badge ${tierLabel} (x${tierNum}) ${res.data.badge_awarded ? 'awarded' : 'confirmed'}.`
      );
      fetchNotifs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setApproving(null);
    }
  };

  if (loading) return <div className="p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;

  return (
    <div data-testid="notifications-panel">
      <div className="flex items-center justify-between p-3 border-b border-border">
        <h3 className="text-sm font-heading uppercase tracking-wider text-foreground flex items-center gap-2">
          <Bell className="w-4 h-4 text-primary" />
          {language === 'fr' ? 'Alertes' : 'Alerts'}
          {unread > 0 && <span className="text-xs bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full">{unread}</span>}
        </h3>
        {unread > 0 && (
          <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read-btn">
            {language === 'fr' ? 'Tout marquer lu' : 'Mark all read'}
          </button>
        )}
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        {notifications.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground text-center">
            {language === 'fr' ? 'Aucune alerte' : 'No alerts'}
          </p>
        ) : (
          notifications.slice(0, 20).map((n, idx) => (
            <div
              key={n.notification_id || `notif-${idx}`}
              className={`p-3 border-b border-border/30 ${n.read ? 'opacity-60' : ''} ${
                n.type === 'return_to_combat' && !n.read && !n.approved
                  ? 'bg-emerald-500/5 border-l-2 border-l-emerald-500'
                  : ''
              }`}
              data-testid={`notification-${n.notification_id || idx}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 rounded-full shrink-0 mt-1 ${
                  n.type === 'return_to_combat' ? 'bg-emerald-500' :
                  n.alert_type === 'red' ? 'bg-red-500' : 'bg-yellow-500'
                }`} />
                <div className="flex-1 min-w-0">
                  {n.title && (
                    <p className="text-xs font-heading uppercase tracking-tight text-foreground mb-0.5">{n.title}</p>
                  )}
                  <p className="text-xs text-foreground/80">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(n.created_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                  </p>

                  {/* RETOUR AU COMBAT — Approval Button */}
                  {n.type === 'return_to_combat' && !n.approved && !n.read && (
                    <Button
                      size="sm"
                      onClick={() => approveReturn(n)}
                      disabled={approving === (n.notification_id || `${n.data?.user_id}-${n.data?.zone}`)}
                      className="mt-2 h-8 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-heading uppercase tracking-wider text-[10px]"
                      data-testid={`approve-return-${n.data?.user_id}-${n.data?.zone}`}
                    >
                      {approving === (n.notification_id || `${n.data?.user_id}-${n.data?.zone}`) ? (
                        <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      ) : (
                        <Swords className="w-3 h-3 mr-1.5" />
                      )}
                      {language === 'fr' ? 'Autoriser le Retour au Combat' : 'Authorize Return to Combat'}
                    </Button>
                  )}

                  {n.type === 'return_to_combat' && n.approved && (
                    <div className="mt-2 flex items-center gap-1.5 text-emerald-400">
                      <Shield className="w-3 h-3" />
                      <span className="text-[10px] font-bold uppercase">
                        {language === 'fr' ? 'Redeploye' : 'Redeployed'}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 h-7 px-2 text-xs"
                  onClick={() => onSelectClient?.(n.client_id || n.data?.user_id)}
                  data-testid={`notif-view-${n.notification_id}`}
                >
                  <Eye className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
      {celebrationBadge && (
        <BadgeCelebration
          badge={celebrationBadge}
          onClose={() => setCelebrationBadge(null)}
          language={language}
        />
      )}
    </div>
  );
}

// ============ MAIN MESSAGING PAGE ============
export default function Messaging() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('messages'); // 'messages' | 'alerts'
  const [mobileShowChat, setMobileShowChat] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const isCoach = user?.role === 'coach' || user?.role === 'admin' || isSuperAdmin;

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get('/messages/conversations');
      setConversations(res.data.conversations);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const handleSelectConvo = (id) => {
    setSelectedConvo(id);
    setMobileShowChat(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="messaging-page">
        <div className="mb-4">
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-foreground flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-primary" />
            {language === 'fr' ? 'Messagerie' : 'Messaging'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSuperAdmin
              ? (language === 'fr' ? 'Supervision de toutes les conversations' : 'All conversations supervision')
              : (language === 'fr' ? 'Communication directe' : 'Direct communication')}
          </p>
        </div>

        {/* Tabs for Coach */}
        {isCoach && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setTab('messages')}
              data-testid="tab-messages"
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase transition-all ${
                tab === 'messages' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />
              {language === 'fr' ? 'Messages' : 'Messages'}
            </button>
            <button
              onClick={() => setTab('alerts')}
              data-testid="tab-alerts"
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase transition-all ${
                tab === 'alerts' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
              }`}
            >
              <Bell className="w-3.5 h-3.5 inline mr-1.5" />
              {language === 'fr' ? 'Alertes' : 'Alerts'}
            </button>
          </div>
        )}

        {/* Alerts Tab */}
        {tab === 'alerts' && isCoach && (
          <Card className="bg-card border-border">
            <NotificationsPanel language={language} />
          </Card>
        )}

        {/* Messages Tab */}
        {tab === 'messages' && (
          <Card className="bg-card border-border overflow-hidden" style={{ height: 'calc(100dvh - 220px)', minHeight: '400px' }}>
            <div className="flex h-full">
              {/* Left Panel - Conversations */}
              <div className={`w-full sm:w-80 border-r border-border flex-shrink-0 ${mobileShowChat ? 'hidden sm:flex sm:flex-col' : 'flex flex-col'}`}>
                <ConversationList
                  conversations={conversations}
                  selected={selectedConvo}
                  onSelect={handleSelectConvo}
                  language={language}
                  isSuperAdmin={isSuperAdmin}
                />
              </div>

              {/* Right Panel - Chat */}
              <div className={`flex-1 flex flex-col ${!mobileShowChat ? 'hidden sm:flex' : 'flex'}`}>
                {mobileShowChat && (
                  <div className="sm:hidden p-2 border-b border-border">
                    <Button variant="ghost" size="sm" onClick={() => setMobileShowChat(false)} data-testid="back-to-list">
                      <ArrowLeft className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Retour' : 'Back'}
                    </Button>
                  </div>
                )}
                {selectedConvo ? (
                  <ChatView
                    conversationId={selectedConvo}
                    language={language}
                    currentUserId={user?.user_id}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">{language === 'fr' ? 'Selectionne une conversation' : 'Select a conversation'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
